/* eslint-disable @typescript-eslint/no-unused-vars */
import { useRef } from 'react'
import {
  Diagram,
  createLinkDefault,
  COMPONENT_DEFAULT_TYPE,
  createArrowMarker,
  ISettings,
  RootStore,
} from 'react-easy-diagram'

import { Nodes, Port } from './components'

import styles from './diagram.module.css'

import { AgentDiagramProps } from './diagram.type'

export const AgentDiagram = ({
  isLoading,
  agents,
  onNodeDoubleClick,
  onAddLink,
  onRemoveLink,
  onDragEnded,
}: AgentDiagramProps) => {
  const diagramRef = useRef<HTMLDivElement>(null)
  const storeRef = useRef<RootStore>(null)

  const getAgentUuidFromNodeId = (nodeId: string) => {
    const agent = agents.find((a) => `${a.role}_${a.name}` === nodeId)
    return agent?.uuid
  }

  if (isLoading) {
    return <div className={styles.core}>Loading...</div>
  }

  const nodes = agents.map((agent, index) => ({
    id: `${agent.role}_${agent.name}`,
    position:
      agent?.diagramPosition || ([100, 100 + index * 100] as [number, number]),
    type: agent?.type,
    data: {
      role: agent.role,
      name: agent.name,
      isActive: agent.active,
      type: agent.type,
      uuid: agent.uuid,
      onNodeDoubleClick,
    },
  }))

  const links = agents.reduce(
    (acc, agent) => {
      if (agent.nextAgentUuid) {
        const nextAgent = agents.find((a) => a.uuid === agent.nextAgentUuid)
        const originNodeId = `${agent.role}_${agent.name}`
        const destinationNodeId = `${nextAgent?.role}_${nextAgent?.name}`

        if (nextAgent) {
          acc.push({
            source: {
              nodeId: originNodeId,
              portId: 'output',
            },
            target: {
              nodeId: destinationNodeId,
              portId: 'input',
            },
          })
        }
      }

      return acc
    },
    [] as {
      source: { nodeId: string; portId: string }
      target: { nodeId: string; portId: string }
    }[],
  )

  const settings: ISettings = {
    callbacks: {
      onLinksAddResult: (info) => {
        if (onAddLink && info.addedLinks.length > 0) {
          info.addedLinks.forEach((link) => {
            const sourceUuid = getAgentUuidFromNodeId(link.source?.nodeId || '')
            const targetUuid = getAgentUuidFromNodeId(link.target?.nodeId || '')

            if (sourceUuid && targetUuid) {
              onAddLink(sourceUuid, targetUuid)
            }
          })
        }
      },
      onLinksRemoveResult: (info) => {
        if (onRemoveLink && info.removedLinks.length > 0) {
          info.removedLinks.forEach((link) => {
            const sourceUuid = getAgentUuidFromNodeId(link.source?.nodeId || '')
            const targetUuid = getAgentUuidFromNodeId(link.target?.nodeId || '')

            if (sourceUuid && targetUuid) {
              onRemoveLink(sourceUuid, targetUuid)
            }
          })
        }
      },
      onDragEnded: ({ nodes }) => {
        if (onDragEnded && nodes.length > 0) {
          const agentUuid = getAgentUuidFromNodeId(nodes?.[0]?.id)
          const position = nodes?.[0]?.position

          const x = position?.[0]
          const y = position?.[1]

          if (agentUuid) {
            onDragEnded(agentUuid, [+x, +y])
          }
        }
      },
    },
    ports: {
      components: {
        oval: Port,
      },
    },
    links: {
      components: {
        [COMPONENT_DEFAULT_TYPE]: createLinkDefault({
          mainLine: {
            classes: {
              base: [styles['animated-link']],
            },
            style: {
              base: {
                stroke: 'var(--primary-400)',
                strokeWidth: 3,
                markerEnd: 'url(#animated_arrow)',
              },
              hovered: {
                stroke: 'var(--primary-600)',
                strokeWidth: 4,
                filter: 'drop-shadow(0 0 4px var(--primary-300))',
              },
            },
          },
        }),
      },
      svgMarkers: [
        createArrowMarker({
          id: 'animated_arrow',
          style: {
            fill: 'var(--primary-400)',
          },
        }),
      ],
    },
    nodes: {
      components: {
        flow: {
          component: Nodes,
          settings: {
            ports: [
              { id: 'input', position: 'left-center', type: 'oval' },
              { id: 'output', position: 'right-center', type: 'oval' },
            ],
          },
        },
        system: {
          component: Nodes,
          settings: {
            ports: [
              { id: 'input', position: 'left-center', type: 'oval' },
              { id: 'output', position: 'right-center', type: 'oval' },
            ],
          },
        },
      },
    },
  }

  return (
    <div ref={diagramRef} className={styles.core}>
      <Diagram
        storeRef={storeRef}
        initState={{
          nodes,
          links,
        }}
        settings={settings}
      />
    </div>
  )
}
